import { useEffect, useMemo, useState } from 'preact/hooks';
import { ApiError, api } from '../../lib/api.js';
import { Button } from '../../components/Button.jsx';

/**
 * Save the current converter colour to an ink in a book.
 * - Pick a book → existing inks + registry names become autocomplete suggestions.
 * - Choose an existing name → updates that ink.
 * - Choose a new name → creates a new ink in the selected book.
 *
 * The current LAB + hex are shown but not editable here — what's on the
 * Converter page is what gets saved.
 */
export const SaveInkModal = ({ open, onClose, onSaved, lab, hex, defaultInkId }) => {
  const [books, setBooks] = useState([]);
  const [bookId, setBookId] = useState(null);
  const [bookDetail, setBookDetail] = useState(null);
  const [name, setName] = useState('');
  const [pantoneCode, setPantoneCode] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Load all books on mount
  useEffect(() => {
    if (!open) return;
    api.get('/ink-books').then(setBooks).catch(setError);
  }, [open]);

  // Load book detail when book changes (for autocomplete)
  useEffect(() => {
    if (!bookId) { setBookDetail(null); return; }
    api.get(`/ink-books/${bookId}`).then(setBookDetail).catch(setError);
  }, [bookId]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setError(null);
      setName('');
      setPantoneCode('');
      setNotes('');
      setBookId(null);
      setBookDetail(null);
      setShowSuggestions(false);
    }
  }, [open]);

  // If this session was loaded from an existing ink, preselect its book +
  // name (and carry over its pantone/notes) so Save defaults to updating it
  // rather than silently blanking those fields.
  useEffect(() => {
    if (!open || !defaultInkId) return;
    api.get(`/ink-books/inks/${defaultInkId}`)
      .then((ink) => {
        setBookId(ink.bookId);
        setName(ink.name);
        setPantoneCode(ink.pantoneCode ?? '');
        setNotes(ink.notes ?? '');
      })
      .catch(setError);
  }, [open, defaultInkId]);

  // Auto-fill pantone code if name looks like "Pantone XXX C"
  useEffect(() => {
    const m = name.match(/Pantone\s+(.+?)\s+([CU])$/i);
    if (m) setPantoneCode(`${m[1]} ${m[2].toUpperCase()}`);
    else if (pantoneCode && !name.match(/Pantone/i)) setPantoneCode('');
  }, [name]);

  // Build autocomplete suggestions: existing inks first, then registry names
  const suggestions = useMemo(() => {
    if (!bookDetail || !name.trim()) return [];
    const q = name.trim().toLowerCase();
    const existingInks = bookDetail.inks
      .filter((i) => i.name.toLowerCase().includes(q))
      .slice(0, 8)
      .map((i) => ({ kind: 'existing', name: i.name, ink: i }));
    const seen = new Set(bookDetail.inks.map((i) => i.name.toLowerCase()));
    const registry = bookDetail.registryNames
      .filter((n) => n.toLowerCase().includes(q) && !seen.has(n.toLowerCase()))
      .slice(0, 12)
      .map((n) => ({ kind: 'registry', name: n, ink: null }));
    return [...existingInks, ...registry];
  }, [bookDetail, name]);

  const existingMatch = bookDetail?.inks.find(
    (i) => i.name.toLowerCase() === name.trim().toLowerCase(),
  );

  const handleSave = async () => {
    if (!bookId || !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        targetLab: { l: lab[0], a: lab[1], b: lab[2] },
        targetHex: hex,
        pantoneCode: pantoneCode.trim() || null,
        notes: notes.trim() || null,
      };
      const saved = existingMatch
        ? await api.patch(`/ink-books/inks/${existingMatch.id}`, payload)
        : await api.post(`/ink-books/${bookId}/inks`, payload);
      onSaved?.(saved);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div class="w-full max-w-md overflow-hidden rounded-xl border border-border-subtle bg-bg-base shadow-xl">

        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: '#FF8A4C' }}>Save to ink</p>
          <div class="flex items-center justify-between gap-3">
            <p class="m-0 font-serif text-lg font-medium text-text-primary">
              LAB {lab.map((v) => v.toFixed(1)).join(', ')}
            </p>
            <div class="flex items-center gap-2 font-mono text-[13px] text-text-tertiary">
              <span class="block h-6 w-6 rounded border border-border-subtle" style={{ background: hex }} />
              {hex}
            </div>
          </div>
        </div>

        <div class="space-y-4 p-5">

          {/* Book picker */}
          <div>
            <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">Ink book</p>
            <select
              value={bookId ?? ''}
              onChange={(e) => setBookId(e.currentTarget.value ? Number(e.currentTarget.value) : null)}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            >
              <option value="">Select a book…</option>
              {books.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          {/* Name + autocomplete */}
          <div class="relative">
            <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">Ink name</p>
            <input
              type="text"
              value={name}
              onInput={(e) => { setName(e.currentTarget.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              disabled={!bookId}
              placeholder={bookId ? 'Type a name or search the registry…' : 'Pick a book first'}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-50"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div class="absolute left-0 right-0 z-10 mt-1 max-h-64 overflow-y-auto rounded-md border border-border-subtle bg-bg-card shadow-lg">
                {suggestions.map((s) => (
                  <button
                    key={`${s.kind}-${s.name}`}
                    type="button"
                    onClick={() => { setName(s.name); setShowSuggestions(false); }}
                    class="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-bg-hover"
                  >
                    <span class="text-sm font-medium text-text-body">{s.name}</span>
                    <span class={`text-[10px] font-semibold uppercase tracking-eyebrow ${s.kind === 'existing' ? 'text-status-ok' : 'text-text-muted'}`}>
                      {s.kind === 'existing' ? 'Saved' : 'Registry'}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {existingMatch && (
              <p class="mt-2 text-[12px] font-semibold text-brand">
                Will update existing ink in {bookDetail?.name}. Target LAB + hex will overwrite.
              </p>
            )}
          </div>

          {/* Pantone code */}
          <div>
            <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">
              Pantone code <span class="font-medium text-text-muted">· optional, auto-filled from name</span>
            </p>
            <input
              type="text"
              value={pantoneCode}
              onInput={(e) => setPantoneCode(e.currentTarget.value)}
              placeholder="e.g. 7427 C"
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          {/* Notes */}
          <div>
            <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">
              Notes <span class="font-medium text-text-muted">· optional</span>
            </p>
            <textarea
              rows={2}
              value={notes}
              onInput={(e) => setNotes(e.currentTarget.value)}
              class="block w-full resize-none rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : error.message || 'Failed to save'}
            </p>
          )}

        </div>

        <div class="flex justify-end gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={busy || !bookId || !name.trim()}>
            {busy ? 'Saving…' : existingMatch ? 'Update ink' : 'Create ink'}
          </Button>
        </div>
      </div>
    </div>
  );
};
